/**
 * File: src/layout/GuestLayout/index.js
 * Layer: Layout
 * Description: Wraps guest portal pages with the shared shell and preloads the
 * authenticated guest evidence request data for child pages.
 */
import { useEffect, useLayoutEffect, useMemo } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

// material-ui
import { styled, useTheme } from '@mui/material/styles';
import { AppBar, Box, Container, CssBaseline, Toolbar, useMediaQuery } from '@mui/material';

// project imports
import Header from './Header';
import Breadcrumbs from 'ui-component/extended/Breadcrumbs';
import SetPasswordDialog from 'views/pages/authentication/auth-forms/SetPasswordDialog';

import navigation from 'menu-items';
import LAYOUT_CONST from 'constant';
import useConfig from 'hooks/useConfig';
import { openDrawer } from 'store/slices/menu';
import { useDispatch, useSelector } from 'store';

// assets
import { IconChevronRight } from '@tabler/icons-react';
import useAuth from 'hooks/useAuth';
import SessionTimeoutPassword from 'ui-component/modals/SessionTimeoutPassword';
// import pageItems from 'page-items';
// import RolePermission from 'utils/permission';
import BackDropLoader from 'ui-component/BackDropLoader';
import menuItems from 'menu-items';
import { fetchGuestEvidence } from 'store/slices/guest-evidence';

// styles
const Main = styled('main', { shouldForwardProp: (prop) => !['open', 'layout'].includes(prop) })(({ theme, open }) => ({
    ...theme.typography.mainContent,
    minHeight: 0,
    width: 'calc(100% - 40px)',
    marginTop: theme.spacing(0.5),
    marginLeft: '20px',
    marginRight: '20px',
    paddingTop: '0',
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    transition: theme.transitions.create('margin', {
        easing: open ? theme.transitions.easing.easeOut : theme.transitions.easing.sharp,
        duration: theme.transitions.duration.shorter + 200
    }),
    [theme.breakpoints.down('md')]: {
        padding: '16px'
    },
    [theme.breakpoints.down('sm')]: {
        width: 'calc(100% - 20px)',
        marginLeft: '10px',
        marginRight: '10px',
        padding: '16px',
        marginTop: theme.spacing(2)
    }
}));

// ==============================|| MAIN LAYOUT ||============================== //

const GuestLayout = () => {
    const { isSessionTimeout, user } = useAuth();
    const theme = useTheme();

    const matchDownMd = useMediaQuery(theme.breakpoints.down('md'));

    const dispatch = useDispatch();
    const { drawerOpen } = useSelector((state) => state.menu);
    const { detail: guestEvidenceDetail } = useSelector((state) => state.guestEvidence);
    const { drawerType, container, layout } = useConfig();

    // get the current path
    const location = useLocation();
    const currentPath = location.pathname;

    const { clientSettings } = useSelector((state) => state.settings);
    const { trial = false } = clientSettings;

    /**
     * This will redirect to default routes
     * If not authorized to view the page that is not belong to the menu
     */
    // Authorization check for non-menu routes removed (no longer used)

    /**
     * This will redirect to default routes for trial direct access
     * If not authorized to view the page
     */
    useLayoutEffect(() => {
        const { items = [] } = menuItems;
        const item = items.find((i) => i.url === currentPath);
        if (trial && item) {
            const { permissions } = item;
            if (permissions?.disableInTrial) {
                window.location.assign('/');
            }
        }
    }, [currentPath, trial]);

    useEffect(() => {
        if (drawerType === LAYOUT_CONST.DEFAULT_DRAWER) {
            dispatch(openDrawer(true));
        } else {
            dispatch(openDrawer(false));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [drawerType]);

    useEffect(() => {
        if (drawerType === LAYOUT_CONST.DEFAULT_DRAWER) {
            dispatch(openDrawer(true));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (matchDownMd) {
            dispatch(openDrawer(true));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [matchDownMd]);

    useEffect(() => {
        if (user?.principalType !== 'guest' || !user?._id) return;

        // Load the guest evidence request once per authenticated guest session
        // so both guest pages can read the same joined request/assessment data.
        if (guestEvidenceDetail?.request?._id === user._id) return;

        dispatch(fetchGuestEvidence()).catch(() => {});
    }, [dispatch, guestEvidenceDetail?.request?._id, user?._id, user?.principalType]);

    const condition = layout === LAYOUT_CONST.HORIZONTAL_LAYOUT && !matchDownMd;

    const header = useMemo(
        () => (
            <Toolbar
                sx={{
                    p: condition ? '10px' : '16px',
                    px: { xs: 1.5, sm: 2 },
                    minWidth: 0,
                    bgcolor: theme.palette.mode !== 'dark' ? theme.palette.secondary.main : theme.palette.background.default
                }}
            >
                <Header />
            </Toolbar>
        ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [layout, matchDownMd]
    );

    return (
        <Box
            sx={{
                display: 'flex',
                minHeight: '100vh',
                flexDirection: 'column',
                bgcolor: theme.palette.mode === 'dark' ? theme.palette.dark[800] : theme.palette.grey[100]
            }}
        >
            <CssBaseline />
            {/* header */}
            <AppBar enableColorOnDark position="sticky" color="inherit" elevation={0} sx={{ bgcolor: theme.palette.background.default }}>
                {header}
            </AppBar>

            {/* main content */}
            <Main open={drawerOpen}>
                <Container maxWidth={container ? 'lg' : false} {...(!container && { sx: { px: { xs: 0 } } })}>
                    {/* breadcrumb */}
                    <Breadcrumbs separator={IconChevronRight} navigation={navigation} icon title rightAlign />
                    <Outlet />
                </Container>
            </Main>
            {/* <Customization /> */}

            {/* Re Enter Password if Session Timeout */}
            <SessionTimeoutPassword toggleDialog={isSessionTimeout} />

            <SetPasswordDialog />

            {/* For Back Drop */}
            <BackDropLoader />
        </Box>
    );
};

export default GuestLayout;
